import React, { useCallback, useEffect, useRef, useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import _ from 'lodash';
import { Grid } from '@mui/material';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import PushForm from '../PushForm';
import PushPreviewer from '../previewer/PushPreviewer';
import DashboardLayout from '../../../../layouts/Dashboard';
import { mobilizeiPushNotificationsService } from '../../../../services/mobilizei/pushNotifications.service';
import { useUser } from "../../../../contexts/user.context";
import { showApiError } from '../../../../utils/errors';
import useAppInfo from '../../../../hooks/appInfo.hook';
import { mobilizeiDevicesService } from '../../../../services/mobilizei/devices.service';
import UnpublishedAppCard from '../trial/UnpublishedAppCard';
import PreviewerDevicesField from '../trial/PreviewerDevicesField';
import PairPreviewerDialog from '../trial/PairPreviewerDialog';

// How long a device that was just paired stays highlighted on the list.
const FRESH_DEVICE_HIGHLIGHT_MS = 5000;

export default function PushNew() {
    const { user } = useUser();
    const { isAppPublished } = useAppInfo();
    const navigate = useNavigate();

    // The app is not published, so the push only reaches the Visualizador devices.
    const isTrial = !isAppPublished();
    const platformId = user?.store?.platformId;
    const storeId = user?.store?.id;
    const location = useLocation();

    // When coming from the "Duplicate" action, prefill the form with the
    // original notification's content (never its schedule/status).
    const duplicateFrom = location.state?.duplicateFrom;
    const isEditing = location.state?.isEditing

    const [payload, setPayload] = useState(() => {
        if (duplicateFrom) {
            return {
                title: duplicateFrom.title || '',
                text: duplicateFrom.text || '',
                action: duplicateFrom.action || 'noAction',
                link: duplicateFrom.link || '',
                linkName: duplicateFrom.linkName || '',
                productUrl: duplicateFrom.productUrl || '',
                image: duplicateFrom.image || undefined,
                showPushModal: duplicateFrom.showPushModal || false,
            };
        }

        return { title: '', text: '', action: 'noAction' };
    });

    const [sendType, setSendType] = useState(isEditing ? duplicateFrom.sendType : 'now');
    const [scheduledFor, setScheduledFor] = useState(isEditing ? duplicateFrom.scheduledFor : '');
    const [isSending, setIsSending] = useState(false);

    // Trial: the push only reaches the devices with the Visualizador connected to
    // the store, and the merchant picks which of them get it.
    const [previewerDevices, setPreviewerDevices] = useState([]);
    const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
    const [removingDeviceIds, setRemovingDeviceIds] = useState(new Set());
    const [freshDeviceId, setFreshDeviceId] = useState(null);
    const [isPairDialogOpen, setIsPairDialogOpen] = useState(false);
    const [pairSince, setPairSince] = useState(null);
    const freshTimeoutRef = useRef();

    const loadPreviewerDevices = useCallback(() => {
        return mobilizeiDevicesService.getPreviewerDevices(platformId, storeId)
            .then((response) => {
                const devices = response?.devices || [];
                setPreviewerDevices(devices);
                return devices;
            });
    }, [platformId, storeId]);

    // Every device starts ticked: the merchant is most likely holding one of them.
    useEffect(() => {
        if (!isTrial || !platformId || !storeId) return;

        loadPreviewerDevices()
            .then((devices) => setSelectedDeviceIds(new Set(devices.map((d) => d.id))))
            .catch((e) => showApiError(e?.error));
    }, [isTrial, platformId, storeId, loadPreviewerDevices]);

    useEffect(() => () => clearTimeout(freshTimeoutRef.current), []);

    function handleToggleDevice(deviceId) {
        setSelectedDeviceIds((ids) => {
            const next = new Set(ids);
            next.has(deviceId) ? next.delete(deviceId) : next.add(deviceId);
            return next;
        });
    }

    function handleRemoveDevice(deviceId) {
        setRemovingDeviceIds((ids) => new Set(ids).add(deviceId));

        mobilizeiDevicesService.removePreviewerDevice(platformId, storeId, deviceId)
            .then(() => {
                setPreviewerDevices((devices) => devices.filter((d) => d.id !== deviceId));
                setSelectedDeviceIds((ids) => {
                    const next = new Set(ids);
                    next.delete(deviceId);
                    return next;
                });
            })
            .catch((e) => showApiError(e?.error))
            .finally(() => {
                setRemovingDeviceIds((ids) => {
                    const next = new Set(ids);
                    next.delete(deviceId);
                    return next;
                });
            });
    }

    // The dialog waits for a device that opens the store after the latest check-in
    // on the list, so the list is refreshed first: a phone opened while the merchant
    // was writing would otherwise close the dialog as soon as it opens.
    function handleOpenPairDialog() {
        const latestAccess = (devices) => devices.reduce(
            (latest, d) => (!latest || d.lastAccessAt > latest ? d.lastAccessAt : latest),
            null
        );

        loadPreviewerDevices()
            .then((devices) => setPairSince(latestAccess(devices)))
            .catch(() => setPairSince(latestAccess(previewerDevices)))
            .finally(() => setIsPairDialogOpen(true));
    }

    // Either a new device or a listed one opened again: it comes back from the list
    // on top, as the most recently seen one, and joins ticked and highlighted for a
    // moment.
    function handleDevicePaired(device) {
        setIsPairDialogOpen(false);
        setSelectedDeviceIds((ids) => new Set(ids).add(device.id));
        setFreshDeviceId(device.id);

        clearTimeout(freshTimeoutRef.current);
        freshTimeoutRef.current = setTimeout(() => setFreshDeviceId(null), FRESH_DEVICE_HIGHLIGHT_MS);

        loadPreviewerDevices().catch((e) => showApiError(e?.error));
    }

    const selectedDevicesCount = previewerDevices.filter((d) => selectedDeviceIds.has(d.id)).length;

    const handlePayloadChange = (propertyArray) => {
        let newPayload = JSON.parse(JSON.stringify(payload));

        for (const item of propertyArray) {
            _.set(newPayload, item.property, item.value);

            if (item.property === 'action') {
                _.set(newPayload, 'link', '');
                _.set(newPayload, 'productUrl', '');
            }
        }

        setPayload(newPayload);
    }

    const handleScheduleChange = ({ sendType: newSendType, scheduledFor: newScheduledFor }) => {
        if (newSendType !== undefined) setSendType(newSendType);
        if (newScheduledFor !== undefined) setScheduledFor(newScheduledFor);
    }

    // Same flow as a published app, on trial or not: the backend saves the push on
    // the history and the merchant goes back to the list to find it there.
    function handleTrialSend() {
        setIsSending(true);

        const deviceIds = previewerDevices.filter((d) => selectedDeviceIds.has(d.id)).map((d) => d.id);

        mobilizeiDevicesService.sendTrialPush(platformId, storeId, { ...payload, deviceIds })
            .then((response) => {
                const count = response?.successCount ?? deviceIds.length;
                toast.success(`Notificação enviada para ${count} ${count === 1 ? 'celular' : 'celulares'}!`);
                navigate('/push');
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsSending(false);
            })
    }

    function handleSend() {
        if (isTrial) {
            handleTrialSend();
            return;
        }

        setIsSending(true);

        const body = { ...payload };

        // The form holds a local datetime; the API expects ISO/UTC.
        if (sendType === 'scheduled' && scheduledFor) {
            body.scheduledFor = new Date(scheduledFor).toISOString();
        }

        mobilizeiPushNotificationsService.sendPushNotification(
            user?.store?.platformId,
            user?.store?.id,
            body
        )
            .then(() => {
                toast.success(sendType === 'scheduled'
                    ? 'Notificação agendada com sucesso!'
                    : 'Notificação enviada com sucesso!');
                navigate('/push');
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsSending(false);
            })
    }

    function handleEdit() {
        setIsSending(true);

        const body = { ...payload };

        if (sendType === 'scheduled' && scheduledFor) {
            body.scheduledFor = new Date(scheduledFor).toISOString();
        }

        mobilizeiPushNotificationsService.editPushNotification(
            user?.store?.platformId,
            user?.store?.id,
            duplicateFrom.id,
            body
        )
            .then(() => {
                toast.success('Notificação salva com sucesso!');
                navigate('/push');
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsSending(false);
            })
    }

    return (
        <DashboardLayout
            Icon={SendIcon}
            title="Criar notificação push"
            subtitle={isTrial
                ? 'Escreva uma mensagem e teste o envio nos seus celulares com o Visualizador Mobilizei'
                : 'Escreva uma mensagem para os usuários do seu aplicativo e escolha entre enviar agora ou agendar para mais tarde'
            }
        >
            {isTrial && <UnpublishedAppCard />}

            <Grid container>
                <Grid item md={5} xs={12}>
                    <PushForm
                        isSending={isSending}
                        onPayloadChange={handlePayloadChange}
                        onSend={handleSend}
                        payload={payload}
                        showSendButton
                        showScheduling
                        sendType={sendType}
                        scheduledFor={scheduledFor}
                        onScheduleChange={handleScheduleChange}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        trialDevicesCount={previewerDevices.length}
                        trialSelectedDevicesCount={selectedDevicesCount}
                        trialRecipients={
                            <PreviewerDevicesField
                                devices={previewerDevices}
                                selectedIds={selectedDeviceIds}
                                freshDeviceId={freshDeviceId}
                                removingIds={removingDeviceIds}
                                onToggle={handleToggleDevice}
                                onRemove={handleRemoveDevice}
                                onPair={handleOpenPairDialog}
                            />
                        }
                    />
                </Grid>

                <Grid item md={7} xs={12}>
                    <PushPreviewer payload={payload} />
                </Grid>
            </Grid>

            {isTrial &&
                <PairPreviewerDialog
                    open={isPairDialogOpen}
                    isFirstDevice={previewerDevices.length === 0}
                    since={pairSince}
                    onClose={() => setIsPairDialogOpen(false)}
                    onPaired={handleDevicePaired}
                />
            }
        </DashboardLayout>
    )
}
